import {ChangeDetectorRef, Component, HostBinding, Inject, Injector, OnInit, ViewEncapsulation} from '@angular/core';
import {DIALOG_DATA, DialogRef} from '@angular/cdk/dialog';
import {BaseComponent} from '../../../base.component';
import {animationSlideInOut} from '../../../../util/animation/animation.slideInOut';
import {StringUtil} from '../../../../util/string.util';

@Component({
  selector: 'app-select-additives',
  standalone: false,
  templateUrl: './select-additives.component.html',
  styleUrl: './select-additives.component.scss',
  animations: [animationSlideInOut],
  encapsulation: ViewEncapsulation.None,
})
export class SelectAdditivesComponent extends BaseComponent implements OnInit {
  @HostBinding('@slideInOut') animationState = 'open';
  selectedAttributes: any[] = [];
  mandatoryAdditives: any[] = [];
  optionalAdditives: any[] = [];
  isReadOnly: boolean = false;
  protected readonly StringUtil = StringUtil;

  constructor(injector: Injector, public dialogRef: DialogRef<string>, @Inject(DIALOG_DATA) public data: any, private cdr: ChangeDetectorRef) {
    super(injector);
  }

  get totalPrice(): number {
    return this.getTotalPrice();
  }

  get formattedTotalPrice(): string {
    return this.formatPrice(this.getTotalPrice());
  }

  override ngOnInit() {
    super.ngOnInit();
    this.isReadOnly = this.data.isReadOnly || false;
    // Load existing selections first, then organize additives
    // This ensures selected additives are available when organizing
    this.loadExistingSelections();
    this.organizeAdditives();
  }

  organizeAdditives() {
    const servicePrice = this.data.Service?.ServicePrices?.[0];
    if (!servicePrice?.ServicePriceAdditives) {
      return;
    }
    const additiveGroups = new Map<string, any>();
    // Sort ServicePriceAdditives by Priority first
    const sortedAdditives = [...servicePrice.ServicePriceAdditives].sort((a: any, b: any) => {
      const priorityA = a.Priority ?? 0;
      const priorityB = b.Priority ?? 0;
      return priorityA - priorityB;
    });
    sortedAdditives.forEach((servicePriceAdditive: any) => {
      const additiveId = servicePriceAdditive.Additive?.AdditiveId;
      if (!additiveId) {
        return;
      }
      if (!additiveGroups.has(additiveId)) {
        additiveGroups.set(additiveId, {
          Additive: servicePriceAdditive.Additive,
          ServicePriceAdditiveId: additiveId,
          Attributes: [],
          MaxChoice: servicePriceAdditive.Additive?.MaxChoice || 1,
          Priority: servicePriceAdditive.Priority ?? 0
        });
      }
      const attributeData = {
        ServicePriceAdditive: servicePriceAdditive,
        Attribute: servicePriceAdditive.Attribute
      };
      additiveGroups.get(additiveId).Attributes.push(attributeData);
    });
    // Sort attributes within each group by Priority
    additiveGroups.forEach((group: any) => {
      group.Attributes.sort((a: any, b: any) => {
        const priorityA = a.ServicePriceAdditive?.Priority ?? 0;
        const priorityB = b.ServicePriceAdditive?.Priority ?? 0;
        return priorityA - priorityB;
      });
    });
    this.mandatoryAdditives = [];
    this.optionalAdditives = [];
    additiveGroups.forEach((group: any) => {
      if (group.Additive?.Type === 'MANDATORY') {
        this.mandatoryAdditives.push(group);
      } else {
        this.optionalAdditives.push(group);
      }
    });
    // Sort groups by Priority
    this.mandatoryAdditives.sort((a: any, b: any) => (a.Priority ?? 0) - (b.Priority ?? 0));
    this.optionalAdditives.sort((a: any, b: any) => (a.Priority ?? 0) - (b.Priority ?? 0));
    this.mandatoryAdditives.forEach((additive: any) => {
      if (additive.Attributes && additive.Attributes.length > 0) {
        const hasSelection = this.selectedAttributes.some((id: string) =>
          additive.Attributes.some((attr: any) => attr.ServicePriceAdditive?.ServicePriceAdditiveId === id)
        );
        if (!hasSelection && additive.Attributes.length > 0) {
          const firstAttr = additive.Attributes[0];
          if (firstAttr.ServicePriceAdditive?.ServicePriceAdditiveId) {
            this.selectedAttributes.push(firstAttr.ServicePriceAdditive.ServicePriceAdditiveId);
          }
        }
      }
    });
  }

  loadExistingSelections() {
    if (this.data.selectedAdditives && Array.isArray(this.data.selectedAdditives)) {
      this.selectedAttributes = [...this.data.selectedAdditives];
    }
  }

  getSelectedAttributesForAdditive(additive: any): any[] {
    if (!this.isReadOnly) {
      return additive.Attributes || [];
    }
    // In read-only mode, only return selected attributes
    return (additive.Attributes || []).filter((attr: any) =>
      this.isAttributeSelected(attr)
    );
  }

  shouldShowAdditive(additive: any): boolean {
    if (!this.isReadOnly) {
      return true;
    }
    // In read-only mode, only show additives that have at least one selected attribute
    return (additive.Attributes || []).some((attr: any) =>
      this.isAttributeSelected(attr)
    );
  }

  onAttributeSelected(attribute: any, additive: any, event?: Event) {
    if (event) {
      event.stopPropagation();
    }
    if (this.isReadOnly) {
      return;
    }
    if (!this.selectedAttributes) {
      this.selectedAttributes = [];
    }
    const attributeId = attribute.ServicePriceAdditive?.ServicePriceAdditiveId;
    if (!attributeId) {
      return;
    }
    const isOptional = additive.Additive?.Type === 'OPTIONAL' || additive.Additive?.Type !== 'MANDATORY';
    const isMandatory = additive.Additive?.Type === 'MANDATORY';
    let selectedForThisAdditive = this.selectedAttributes.filter(id =>
      additive.Attributes.some((attr: any) => attr.ServicePriceAdditive?.ServicePriceAdditiveId === id)
    );
    const isCurrentlySelected = selectedForThisAdditive.includes(attributeId);
    if (isCurrentlySelected) {
      if (isOptional) {
        this.selectedAttributes = [...this.selectedAttributes.filter(id => id !== attributeId)];
      }
    } else {
      let newSelectedAttributes = [...this.selectedAttributes];
      if (isMandatory) {
        newSelectedAttributes = newSelectedAttributes.filter(id =>
          !additive.Attributes.some((attr: any) => attr.ServicePriceAdditive?.ServicePriceAdditiveId === id)
        );
      } else {
        if (selectedForThisAdditive.length >= (additive.MaxChoice || 999)) {
          if (selectedForThisAdditive.length > 0) {
            newSelectedAttributes = newSelectedAttributes.filter(id => id !== selectedForThisAdditive[0]);
          }
        }
      }
      newSelectedAttributes.push(attributeId);
      this.selectedAttributes = newSelectedAttributes;
    }
    this.cdr.detectChanges();
  }

  isAttributeSelected(attribute: any): boolean {
    const attributeId = attribute.ServicePriceAdditive?.ServicePriceAdditiveId;
    return this.selectedAttributes.includes(attributeId);
  }

  isOptionalAdditive(additive: any): boolean {
    return additive.Additive?.Type === 'OPTIONAL' || additive.Additive?.Type !== 'MANDATORY';
  }

  getTotalPrice(): number {
    let total = 0;
    const servicePrice = this.data.Service?.ServicePrices?.[0];
    if (servicePrice?.NewPrice) {
      total += parseFloat(servicePrice.NewPrice) || 0;
    } else if (servicePrice?.Price) {
      total += parseFloat(servicePrice.Price) || 0;
    }
    this.selectedAttributes.forEach((attributeId: string) => {
      const allAdditives = [...this.mandatoryAdditives, ...this.optionalAdditives];
      for (const additive of allAdditives) {
        const attribute = additive.Attributes?.find((attr: any) =>
          attr.ServicePriceAdditive?.ServicePriceAdditiveId === attributeId
        );
        if (attribute) {
          total += parseFloat(attribute.ServicePriceAdditive?.Price) || 0;
          break;
        }
      }
    });
    return total;
  }

  canSubmit(): boolean {
    for (const additive of this.mandatoryAdditives) {
      const hasSelection = this.selectedAttributes.some((id: string) =>
        additive.Attributes?.some((attr: any) => attr.ServicePriceAdditive?.ServicePriceAdditiveId === id)
      );
      if (!hasSelection) {
        return false;
      }
    }
    return true;
  }

  onSubmit() {
    if (!this.canSubmit()) {
      return;
    }
    if (this.data.onSubmit) {
      this.data.onSubmit(this.data.Service, this.selectedAttributes);
    }
    this.dialogRef.close();
  }

  noAction() {
    this.dialogRef.close();
  }

  getAttributePrice(attribute: any): number {
    return parseFloat(attribute.ServicePriceAdditive?.Price) || 0;
  }

  formatPrice(price: number): string {
    if (price === 0) {
      return 'رایگان';
    }
    const formatted = StringUtil.numberAsCurrency(price) || '0';
    return formatted + ' ریال';
  }
}

